import { ChangeDetectionStrategy, Component, DOCUMENT, inject, OnInit, signal } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ContenuArticle, Section } from '@components/contenu-article/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { HandleHtmlLinksDirective } from '@shared/directives';
import { TranslatePluralPipe } from '@shared/pipe';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';

@Component({
  selector: 'app-contenu-article',
  imports: [TranslatePipe, TranslatePluralPipe, ImageComponent, SvgComponent, HandleHtmlLinksDirective],
  templateUrl: './contenu-article.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContenuArticleComponent extends AbstractDynamicComponent<ContenuArticle> implements OnInit {
  protected readonly realReadingTimeMinutes = signal(0);
  private readonly window = inject(DOCUMENT).defaultView as Window;
  private readonly logs = inject(LogsService);
  private readonly isBrowser = inject(PlatformContextService).isBrowser;

  ngOnInit(): void {
    if (this.isBrowser()) {
      this.realReadingTimeMinutes.set(this.calculateReadingTimeFromSections(this.data.sections));
    }
  }

  openSharePopup(): void {
    const shareData = {
      title: this.data.title,
      text: this.data.description ? this.data.description : '',
      url: this.window.location.href,
    };
    navigator.share(shareData).catch((err: unknown) => {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      this.logs.error('ContenuArticleComponent', 'navigator.share failed', err);
    });
  }

  /**
   * Calculates the estimated reading time from an array of article sections.
   * It accumulates text from HTML sections (title and text) and image sections (legend).
   * @param sections An array of Section objects.
   * @param wordsPerMinute The average reading speed. Defaults to 225.
   * @returns The estimated reading time in minutes.
   */
  private calculateReadingTimeFromSections(sections: Section[] | undefined, wordsPerMinute: number = 225): number {
    // Return 0 if the sections array is null, undefined, or empty
    if (!sections || sections.length === 0) {
      return 0;
    }

    // A helper function to strip HTML tags from a string
    const stripHtml = (html: string): string => {
      // Create a temporary DOM element to parse the HTML string
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Return the text content of the body, or an empty string if null
      return doc.body.textContent || '';
    };

    // Accumulate all text content from the different sections
    const fullText = sections
      .map((section) => {
        switch (section.type) {
          case 'section_html':
            // For HTML sections, combine the title and the sanitized text content
            return `${section.title || ''} ${stripHtml(section.text || '')}`;
          case 'section_image':
            // For image sections, only use the legend if it exists
            return section.legend || '';
          default:
            return '';
        }
      })
      .join(' ');

    // Split the combined text by any whitespace character to count the words
    const wordCount = fullText
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;

    // If there are no words, reading time is 0
    if (wordCount === 0) {
      return 0;
    }

    // Calculate the reading time and round it up to the nearest integer
    const readingTimeMinutes = Math.ceil(wordCount / wordsPerMinute);
    this.logs.debug(
      'ContenuArticleComponent',
      `Reading time for this article is ${readingTimeMinutes} ${readingTimeMinutes > 1 ? 'minutes' : 'minute'}`
    );
    return readingTimeMinutes;
  }
}
